import type { CSSProperties } from 'react';
import {
  AlertTriangle,
  Book,
  Brain,
  Camera,
  Check,
  Clock,
  Droplet,
  Fish,
  FlaskConical,
  Flower2,
  History,
  Image,
  Loader,
  Minus,
  SearchX,
  Sparkles,
  Sun,
  Thermometer,
  ThumbsDown,
  ThumbsUp,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import './Icon.css';

export type IconSize = 'sm' | 'md' | 'lg';

export interface IconProps {
  name: string;
  size?: IconSize | number;
  color?: string;
  ariaLabel?: string;
  className?: string;
}

const SIZE_MAP: Record<IconSize, number> = {
  sm: 16,
  md: 24,
  lg: 32,
};

/**
 * Icon names the atom can render. Named imports keep the bundle tree-shaken;
 * add an entry here when a new icon is needed.
 */
const ICONS = new Map<string, LucideIcon>([
  ['alert-triangle', AlertTriangle],
  ['book', Book],
  ['brain', Brain],
  ['camera', Camera],
  ['check', Check],
  ['clock', Clock],
  ['droplet', Droplet],
  ['fish', Fish],
  ['flask', FlaskConical],
  // Memorial marker for deceased livestock ("Verstorben" badge).
  ['flower', Flower2],
  ['history', History],
  ['image', Image],
  ['loader', Loader],
  ['minus', Minus],
  ['search-off', SearchX],
  ['sparkles', Sparkles],
  ['sun', Sun],
  ['thermometer', Thermometer],
  ['thumbs-down', ThumbsDown],
  ['thumbs-up', ThumbsUp],
  ['wrench', Wrench],
]);

const NAMED_SIZES = new Set<string>(['sm', 'md', 'lg']);

function resolveSize(size: IconSize | number | undefined): number {
  if (size === undefined) return SIZE_MAP['md'];
  if (typeof size === 'number') return size;
  // eslint-disable-next-line security/detect-object-injection
  return SIZE_MAP[size as IconSize];
}

export function Icon({ name, size, color, ariaLabel, className }: IconProps) {
  const isNamedSize = size === undefined || (typeof size === 'string' && NAMED_SIZES.has(size));
  const namedSize: IconSize = (size === undefined ? 'md' : isNamedSize ? (size as IconSize) : 'md');

  const roleProps = !ariaLabel
    ? { role: 'presentation' as const, 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': ariaLabel };

  const style: CSSProperties = isNamedSize
    ? (color ? { color } : {})
    : { width: resolveSize(size), height: resolveSize(size), ...(color ? { color } : {}) };

  const classes = [
    'icon',
    isNamedSize ? `icon--${namedSize}` : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const LucideComponent = ICONS.get(name);
  const sharedProps = {
    className: classes,
    style: Object.keys(style).length > 0 ? style : undefined,
    focusable: 'false' as const,
    ...roleProps,
  };

  if (!LucideComponent) {
    // Unknown name: keep the box so layout does not shift.
    return <svg xmlns="http://www.w3.org/2000/svg" {...sharedProps} />;
  }

  return <LucideComponent size={resolveSize(isNamedSize ? namedSize : size)} {...sharedProps} />;
}
